import { useRef, useCallback, useMemo, useReducer } from "react";
import { useDispatch } from "react-redux";

import { setOpenModalType } from "../store/actions/modal";
import { hideNotificationsExceptUnlock } from "../store/actions/notifications";
import modalTypes from "../enums/modalTypes";
import noop from "../utils/noop";

const CLOSED = "closed";

const ACTIONS = {
  SHOW: "SHOW",
  HIDE: "HIDE",
};

const createNewPromise = () => {
  let resolveLocal;
  let rejectLocal;
  const promise = new Promise((resolve, reject) => {
    resolveLocal = resolve;
    rejectLocal = reject;
  });

  return {
    promise,
    resolve: resolveLocal,
    reject: rejectLocal,
  };
};

const defaultHandlers = {
  onConfirm: noop,
  onClose: noop,
};
const defaultProps = {};

const defaultState = { open: false, additionalProps: defaultProps };

const modalReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SHOW: {
      return {
        open: true,
        additionalProps: action.payload,
      };
    }
    case ACTIONS.HIDE: {
      return {
        open: false,
        additionalProps: defaultProps,
      };
    }

    default:
      return state;
  }
};

const showAction = (props) => ({ type: ACTIONS.SHOW, payload: props });
const hideAction = () => ({ type: ACTIONS.HIDE, payload: null });

const useModalController = (type = modalTypes.DEFAULT) => {
  const promiseRef = useRef();
  const showRef = useRef(noop);
  const reduxDispatch = useDispatch();
  const [{ open, additionalProps }, dispatch] = useReducer(
    modalReducer,
    defaultState
  );

  const onOpen = useCallback(
    (props) => {
      dispatch(showAction(props));
      reduxDispatch(hideNotificationsExceptUnlock());
      reduxDispatch(setOpenModalType(type));
    },
    [reduxDispatch, type]
  );

  const onHide = useCallback(
    (props) => {
      dispatch(hideAction(props));
      reduxDispatch(hideNotificationsExceptUnlock());
      reduxDispatch(setOpenModalType(null));
    },
    [reduxDispatch]
  );

  const handlers = useMemo(() => {
    if (!open) {
      promiseRef.current = createNewPromise();
      showRef.current = () => {
        return promiseRef.current.promise;
      };
    }

    if (!open) return defaultHandlers;

    return {
      onConfirm(result) {
        promiseRef.current.resolve(result);
        onHide();
      },
      onClose() {
        promiseRef.current.reject(new Error(CLOSED));
        onHide();
      },
    };
  }, [onHide, open]);

  const showPromised = useCallback(
    (props) => {
      onOpen(props);
      return showRef.current();
    },
    [onOpen]
  );

  return [
    {
      open,
      onConfirm: handlers.onConfirm,
      onClose: handlers.onClose,
      ...additionalProps,
    },
    showPromised,
  ];
};

export default useModalController;
